
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const signatureCocktails = [
  {
    id: 1,
    name: 'Golden Empress',
    tagline: 'Our Signature Creation',
    description: 'A luxurious blend of aged cognac, champagne reduction, gold-infused honey, and a whisper of saffron. Finished with edible gold leaf.',
    ingredients: ['Rémy Martin XO', 'Dom Pérignon Reduction', 'Saffron Honey', '24K Gold Leaf'],
    price: '$28',
    image: 'https://public.readdy.ai/ai/img_res/b595a8a0a5a87c02e8254b596626cc9d.jpg',
    color: 'from-amber-400 to-yellow-300',
    featured: true,
  },
  {
    id: 2,
    name: 'Velvet Noir',
    tagline: 'Dark & Mysterious',
    description: 'Smoky mezcal meets activated charcoal, balanced with blackberry liqueur and a hint of vanilla. Served in a smoke-filled glass.',
    ingredients: ['Del Maguey Mezcal', 'Activated Charcoal', 'Crème de Mûre', 'Madagascar Vanilla'],
    price: '$24',
    image: 'https://readdy.ai/api/search-image?query=dramatic%20black%20charcoal%20cocktail%20in%20elegant%20rocks%20glass%20with%20smoke%20effect%2C%20dark%20moody%20bar%20setting%2C%20purple%20blackberry%20garnish%2C%20professional%20mixology%20photography%2C%20mysterious%20atmosphere%2C%20premium%20cocktail%20presentation&width=400&height=500&seq=sig-cocktail-002&orientation=portrait',
    color: 'from-stone-600 to-stone-800',
    featured: false,
  },
  {
    id: 3,
    name: 'Rose Petal Martini',
    tagline: 'Floral Elegance',
    description: 'Delicate rose-infused vodka shaken with elderflower liqueur, fresh lemon, and a touch of pink peppercorn. Garnished with crystallized rose petals.',
    ingredients: ['Rose Vodka', 'St-Germain', 'Pink Peppercorn', 'Crystallized Rose'],
    price: '$22',
    image: 'https://readdy.ai/api/search-image?query=beautiful%20pink%20rose%20cocktail%20in%20elegant%20martini%20glass%20with%20rose%20petal%20garnish%20floating%20on%20top%2C%20soft%20romantic%20lighting%2C%20luxury%20bar%20background%2C%20feminine%20elegant%20presentation%2C%20professional%20cocktail%20photography&width=400&height=500&seq=sig-cocktail-003&orientation=portrait',
    color: 'from-rose-400 to-pink-300',
    featured: false,
  },
  {
    id: 4,
    name: 'Amber Reverie',
    tagline: 'Warm & Sophisticated',
    description: 'Premium bourbon infused with toasted oak and caramelized fig, complemented by aromatic bitters and a torched orange peel.',
    ingredients: ['Woodford Reserve', 'Fig Reduction', 'Oak Bitters', 'Torched Orange'],
    price: '$26',
    image: 'https://readdy.ai/api/search-image?query=sophisticated%20amber%20bourbon%20cocktail%20in%20crystal%20old%20fashioned%20glass%20with%20large%20ice%20sphere%20and%20orange%20peel%20garnish%2C%20warm%20golden%20lighting%2C%20luxury%20dark%20wood%20bar%20background%2C%20professional%20cocktail%20photography%2C%20elegant%20presentation&width=400&height=500&seq=sig-cocktail-004&orientation=portrait',
    color: 'from-amber-600 to-orange-400',
    featured: false,
  },
  {
    id: 5,
    name: 'Jade Garden',
    tagline: 'Fresh & Vibrant',
    description: 'A refreshing fusion of Japanese gin, matcha, cucumber essence, and yuzu. Topped with champagne foam and micro shiso.',
    ingredients: ['Roku Gin', 'Ceremonial Matcha', 'Yuzu Juice', 'Champagne Foam'],
    price: '$24',
    image: 'https://readdy.ai/api/search-image?query=elegant%20green%20matcha%20cocktail%20in%20tall%20highball%20glass%20with%20cucumber%20ribbon%20garnish%2C%20fresh%20vibrant%20presentation%2C%20luxury%20minimalist%20bar%20background%2C%20professional%20cocktail%20photography%2C%20zen%20aesthetic%2C%20soft%20natural%20lighting&width=400&height=500&seq=sig-cocktail-005&orientation=portrait',
    color: 'from-emerald-500 to-green-400',
    featured: false,
  },
  {
    id: 6,
    name: 'Midnight Truffle',
    tagline: 'Decadent Indulgence',
    description: 'Rich espresso martini elevated with white truffle oil, dark chocolate bitters, and a salted caramel rim. Pure indulgence.',
    ingredients: ['Grey Goose Vodka', 'Truffle Espresso', 'Chocolate Bitters', 'Salted Caramel'],
    price: '$26',
    image: 'https://readdy.ai/api/search-image?query=luxurious%20dark%20espresso%20martini%20cocktail%20in%20elegant%20coupe%20glass%20with%20coffee%20beans%20and%20chocolate%20garnish%2C%20rich%20creamy%20foam%20top%2C%20moody%20dramatic%20lighting%2C%20premium%20bar%20setting%2C%20professional%20cocktail%20photography&width=400&height=500&seq=sig-cocktail-006&orientation=portrait',
    color: 'from-stone-700 to-amber-900',
    featured: false,
  },
];

export default function SignatureCocktails() {
  const [selectedCocktail, setSelectedCocktail] = useState(signatureCocktails[0]);
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const { ref: menuRef, isVisible: menuVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });

  return (
    <section id="signature-menu" className="py-32 bg-stone-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
      </div>
      
      {/* Decorative Lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div className={`text-center mb-20 scroll-animate-up ${isVisible ? 'visible' : ''}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-amber-500"></div>
            <span className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase">Exclusive Creations</span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-amber-500"></div>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-display font-normal text-white mb-6">
            Signature <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300">Cocktail Menu</span>
          </h2>
          
          <p className="text-white/50 max-w-2xl mx-auto text-lg leading-relaxed">
            Discover our collection of handcrafted signature cocktails, each one a masterpiece 
            developed through years of experimentation and refined taste.
          </p>
        </div>

        {/* Featured Cocktail Showcase */}
        <div className={`mb-20 scroll-animate-up ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '200ms' }}>
          <div className="relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden">
            {/* Decorative corner accents */}
            <div className="absolute top-0 left-0 w-20 h-20 border-l-2 border-t-2 border-amber-500/30 rounded-tl-3xl"></div>
            <div className="absolute bottom-0 right-0 w-20 h-20 border-r-2 border-b-2 border-amber-500/30 rounded-br-3xl"></div>
            
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Image Side */}
              <div className="relative h-[500px] lg:h-auto">
                <img
                  src={selectedCocktail.image}
                  alt={selectedCocktail.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-stone-950 hidden lg:block"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/50 to-transparent lg:hidden"></div>
                
                {/* Featured Badge */}
                {selectedCocktail.featured && (
                  <div className="absolute top-6 left-6 px-4 py-2 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full">
                    <span className="text-stone-900 text-xs font-bold tracking-wider uppercase flex items-center gap-2">
                      <i className="ri-star-fill"></i>
                      House Signature
                    </span>
                  </div>
                )}
              </div>
              
              {/* Content Side */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="space-y-6">
                  <div>
                    <p className="text-amber-400/70 text-sm tracking-wider uppercase mb-2">{selectedCocktail.tagline}</p>
                    <h3 className="text-4xl lg:text-5xl font-display text-white mb-2">{selectedCocktail.name}</h3>
                    <p className="text-3xl font-display text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">
                      {selectedCocktail.price}
                    </p>
                  </div>
                  
                  <p className="text-white/60 leading-relaxed text-lg">
                    {selectedCocktail.description}
                  </p>
                  
                  {/* Ingredients */}
                  <div>
                    <p className="text-amber-400/50 text-xs tracking-wider uppercase mb-3">Key Ingredients</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCocktail.ingredients.map((ingredient, i) => (
                        <span
                          key={i}
                          className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/70 text-sm"
                        >
                          {ingredient}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <div className="pt-4">
                    <a
                      href="#booking"
                      className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-stone-900 font-semibold rounded-full hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-calendar-line"></i>
                      Book a Tasting Experience
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cocktail Grid */}
        <div ref={menuRef} className={`stagger-children ${menuVisible ? 'visible' : ''}`}>
          <div className="flex items-center justify-between mb-10">
            <h3 className="text-2xl font-display text-white">Full Menu</h3>
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <i className="ri-information-line"></i>
              <span>Click to explore</span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {signatureCocktails.map((cocktail) => (
              <button
                key={cocktail.id}
                onClick={() => {
                  setSelectedCocktail(cocktail);
                  document.getElementById('signature-menu')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`group relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] rounded-2xl overflow-hidden border transition-all duration-500 cursor-pointer text-left ${
                  selectedCocktail.id === cocktail.id
                    ? 'border-amber-500/50 shadow-xl shadow-amber-500/10'
                    : 'border-white/5 hover:border-white/20'
                }`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={cocktail.image}
                    alt={cocktail.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/50 to-transparent"></div>
                  
                  {/* Price Badge */}
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full border border-white/10">
                    <span className="text-amber-300 font-semibold text-sm">{cocktail.price}</span>
                  </div>
                  
                  {/* Featured indicator */}
                  {cocktail.featured && (
                    <div className="absolute top-4 left-4">
                      <i className="ri-star-fill text-amber-400"></i>
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <p className="text-amber-400/60 text-xs tracking-wider uppercase mb-1">{cocktail.tagline}</p>
                  <h4 className="text-xl font-display text-white mb-2 group-hover:text-amber-200 transition-colors">
                    {cocktail.name}
                  </h4>
                  <p className="text-white/40 text-sm line-clamp-2">{cocktail.description}</p>
                  
                  {/* Color indicator */}
                  <div className={`mt-4 h-1 w-16 rounded-full bg-gradient-to-r ${cocktail.color} opacity-60 group-hover:opacity-100 group-hover:w-full transition-all duration-500`}></div>
                </div>
                
                {/* Selected indicator */}
                {selectedCocktail.id === cocktail.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-yellow-300"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`mt-16 text-center scroll-animate-up ${menuVisible ? 'visible' : ''}`} style={{ transitionDelay: '400ms' }}>
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 p-8 bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-2xl border border-amber-500/20">
            <div className="text-center sm:text-left">
              <p className="text-white font-display text-xl mb-1">Want to learn these recipes?</p>
              <p className="text-white/50 text-sm">Join our masterclass and craft these cocktails yourself</p>
            </div>
            <a
              href="#classes"
              className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-full hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-graduation-cap-line text-amber-400"></i>
              View Classes
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
